import { createContext } from 'react';
import type { ContextValue } from 'react-aria-components';
import { Button, ButtonProps, useContextProps } from 'react-aria-components';
import { mergeClassNames } from '../../../../utils/mergeClassNames';
import { SelectBaseProps } from './SelectBase';
import styles from './TriggerButton.module.scss';

export type SelectBaseTriggerButtonProps = ButtonProps & {
  /**
   * The size, impacting the button's dimensions and typography.
   * @default 'medium'
   */
  size?: SelectBaseProps['size'];
};

export type SelectBaseTriggerButtonContextValue = ContextValue<
  Omit<SelectBaseTriggerButtonProps, 'children'>,
  HTMLButtonElement
>;

export const SelectBaseTriggerButtonContext =
  createContext<SelectBaseTriggerButtonContextValue>({});

/**
 * A styled Button to use as a trigger for a Select from `react-aria-components`. Use when creating a custom Select. Otherwise, prefer `Select` components.
 *
 * Merges context props from `SelectBaseTriggerButtonContext`.
 */
export const SelectBaseTriggerButton = (
  props: SelectBaseTriggerButtonProps
) => {
  const [contextProps] = useContextProps(
    props,
    null,
    SelectBaseTriggerButtonContext
  );
  const { children, className, size = 'medium', ...otherProps } = contextProps;

  return (
    <Button
      {...otherProps}
      className={(renderProps) =>
        mergeClassNames([styles.trigger, className], renderProps)
      }
      data-size={size}
    >
      {(renderProps) =>
        typeof children === 'function' ? children(renderProps) : children
      }
    </Button>
  );
};
